import http from "../utils/http";
import { GETListResponse } from "./SubscriptionServices";

// User subscription with embedded order info for global view
export interface IUserSubscriptionWithOrder {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  } | null;
  subscription: {
    id: string;
    name: string;
    price: number;
    duration: number;
  } | null;
  startDate: string;
  endDate: string;
  status:
    | "ACTIVE"
    | "PAUSED"
    | "CANCELLED"
    | "EXPIRED"
    | "INACTIVE"
    | "GRANTED";
  createdAt: string;
  cancellationReason: string | null;
  cancelledAt: string | null;
  order: {
    id: string;
    orderId: string;
    paymentStatus:
      | "PENDING"
      | "ATTEMPTED"
      | "AUTHORIZED"
      | "PAID"
      | "PAID_MANUAL"
      | "FAILED"
      | "REFUNDED";
    amount: number;
    refundedAmount: number | null;
    paymentId: string;
    createdAt: string;
  } | null;
}

// Order model for the drawer
export interface IOrder {
  id: string;
  orderId: string;
  receiptId: string;
  userId: string;
  programId: string;
  programName: string;
  paymentId: string;
  createdAt: string;
  updatedAt: string;
  paymentStatus:
    | "PENDING"
    | "ATTEMPTED"
    | "AUTHORIZED"
    | "PAID"
    | "PAID_MANUAL"
    | "FAILED"
    | "REFUNDED";
  paymentMode?: "APP" | "CASH" | "ONLINE";
  paidAt?: string;
  amount: number;
  couponDiscount: number;
  couponCode: string | null;
  discount: number;
  price: number;
  // Refund bookkeeping fields
  refundedAmount?: number;
  refundedAt?: string;
  refundedBy?: string;
  refundReferenceId?: string;
  refundNotes?: string;
  userSubscription?: {
    id: string;
    status:
      | "ACTIVE"
      | "PAUSED"
      | "CANCELLED"
      | "EXPIRED"
      | "INACTIVE"
      | "GRANTED";
    startDate: string;
    endDate: string;
  };
}

export interface IInvoiceLineItem {
  name: string;
  description: string;
  amount: number;
  discount: number;
  total: number;
}

export interface IInvoice {
  business: {
    name: string;
    address: string;
    phone: string;
    email: string;
  };
  receiptNumber: string;
  orderId: string;
  invoiceDate: string;
  customerName: string;
  customerPhone: string;
  lineItems: IInvoiceLineItem[];
  subtotal: number;
  discount: number;
  couponDiscount: number;
  couponCode: string | null;
  total: number;
  paymentMode: "APP" | "CASH" | "ONLINE" | null;
  paymentStatus: "PAID" | "PAID_MANUAL" | "REFUNDED";
  paymentId: string | null;
  refund: {
    refundedAmount: number | null;
    refundedAt: string | null;
  } | null;
}

export interface GetUserSubscriptionsParams {
  page: number;
  size: number;
  status?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface IOrderWithUser {
  id: string;
  orderId: string;
  receiptId: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  } | null;
  programId: string;
  programName: string;
  paymentId: string;
  createdAt: string;
  updatedAt: string;
  paymentStatus:
    | "PENDING"
    | "ATTEMPTED"
    | "AUTHORIZED"
    | "PAID"
    | "PAID_MANUAL"
    | "FAILED"
    | "REFUNDED";
  paymentMode?: "APP" | "CASH" | "ONLINE";
  paidAt?: string;
  amount: number;
  refundedAmount?: number;
  refundedAt?: string;
  refundedBy?: string;
  refundReferenceId?: string;
  refundNotes?: string;
}

export interface GetOrdersParams {
  page: number;
  size: number;
  status?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Get all user subscriptions with filters for the global admin view.
 * Includes embedded order info (payment status, amount).
 */
const GetUserSubscriptionsGlobal = async ({
  page,
  size,
  status,
  search,
  startDate,
  endDate,
}: GetUserSubscriptionsParams): Promise<
  GETListResponse<IUserSubscriptionWithOrder>
> => {
  let urlPath = `/admin/user-subscriptions/global?page=${page}&size=${size}`;

  if (status && status !== "ALL") {
    urlPath += `&status=${status}`;
  }
  if (search && search.trim()) {
    urlPath += `&search=${encodeURIComponent(search.trim())}`;
  }
  if (startDate) {
    urlPath += `&startDate=${startDate}`;
  }
  if (endDate) {
    urlPath += `&endDate=${endDate}`;
  }

  const result = await http.get({ path: urlPath });
  if (result.status === 200) {
    return result.data as GETListResponse<IUserSubscriptionWithOrder>;
  } else {
    throw new Error(`Error fetching user subscriptions: ${result.statusText}`);
  }
};

/**
 * Get global orders list with filters for the admin view.
 * Includes embedded user info.
 */
const GetOrdersGlobal = async ({
  page,
  size,
  status,
  search,
  startDate,
  endDate,
}: GetOrdersParams): Promise<GETListResponse<IOrderWithUser>> => {
  let urlPath = `/admin/orders?page=${page}&size=${size}`;

  if (status && status !== "ALL") {
    urlPath += `&status=${status}`;
  }
  if (search && search.trim()) {
    urlPath += `&search=${encodeURIComponent(search.trim())}`;
  }
  if (startDate) {
    urlPath += `&startDate=${startDate}`;
  }
  if (endDate) {
    urlPath += `&endDate=${endDate}`;
  }

  const result = await http.get({ path: urlPath });
  if (result.status === 200) {
    return result.data as GETListResponse<IOrderWithUser>;
  } else {
    throw new Error(`Error fetching orders: ${result.statusText}`);
  }
};

/**
 * Get a single order by orderId for the detail drawer.
 */
const GetOrderById = async ({
  orderId,
}: {
  orderId: string;
}): Promise<IOrder> => {
  const urlPath = `/admin/orders/${orderId}`;
  const result = await http.get({ path: urlPath });
  if (result.status === 200) {
    return result.data as IOrder;
  } else {
    throw new Error(`Error fetching order: ${result.statusText}`);
  }
};

const GetOrderInvoice = async (orderId: string): Promise<IInvoice> => {
  const urlPath = `/admin/orders/${orderId}/invoice`;
  const result = await http.get({ path: urlPath });
  if (result.status === 200) {
    return result.data as IInvoice;
  }
  throw new Error(`Error fetching invoice: ${result.statusText}`);
};

export interface MarkRefundRequest {
  refundedAmount: number;
  refundReferenceId?: string;
  refundNotes: string;
}

/**
 * Mark an order as refunded (bookkeeping only - no Razorpay integration).
 * Captures refund details: amount refunded, notes (include Razorpay Refund ID).
 */
const MarkOrderAsRefunded = async (
  orderId: string,
  request: MarkRefundRequest
): Promise<IOrder> => {
  const urlPath = `/admin/orders/${orderId}/refund`;
  const result = await http.put({ path: urlPath, body: request });
  if (result.status === 200) {
    return result.data as IOrder;
  } else {
    throw new Error(`Error marking order as refunded: ${result.statusText}`);
  }
};

export {
  GetUserSubscriptionsGlobal,
  GetOrdersGlobal,
  GetOrderById,
  GetOrderInvoice,
  MarkOrderAsRefunded,
};
